import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { X as XIcon } from "@phosphor-icons/react";

const DEPT_BAND = {
  Executive:      "bg-blue-500/10 border-blue-500/40 text-blue-200",
  Consulting:     "bg-teal-500/10 border-teal-500/40 text-teal-200",
  Investigations: "bg-amber-500/10 border-amber-500/40 text-amber-200",
  Legal:          "bg-purple-500/10 border-purple-500/40 text-purple-200",
  Finance:        "bg-slate-500/10 border-slate-500/40 text-slate-200",
  HR:             "bg-slate-500/10 border-slate-500/40 text-slate-200",
  Quality:        "bg-teal-500/10 border-teal-500/40 text-teal-200",
  Operations:     "bg-blue-500/10 border-blue-500/40 text-blue-200",
};

function getBand(dept) {
  return DEPT_BAND[dept] || DEPT_BAND.Operations;
}

function NodeCard(props) {
  const node = props.node;
  const dimmed = props.filter !== "All" && node.department !== props.filter;
  const klass = getBand(node.department);
  return (
    <button
      type="button"
      data-testid={"org-node-" + node.initials}
      onClick={() => props.onClick(node)}
      className={"px-3 py-2 border-2 hover:brightness-125 transition min-w-[180px] max-w-[220px] " + klass + (dimmed ? " opacity-30" : "")}
    >
      <div className="flex items-center gap-2 mb-1">
        <div className="w-9 h-9 flex items-center justify-center font-mono text-[12px] font-bold bg-black/30 border border-current/40 shrink-0">
          {node.initials}
        </div>
        <div className="text-left min-w-0 flex-1">
          <div className="text-[12px] font-semibold truncate">{node.name}</div>
          <div className="text-[10px] opacity-75 truncate">{node.role}</div>
        </div>
      </div>
      <div className="flex flex-wrap gap-1 items-center">
        {node.rm_md === "MD" ? <span className="font-mono text-[8px] tracking-[0.14em] px-1.5 py-px bg-amber-500/20 text-amber-100 border border-amber-500/40">MD</span> : null}
        {node.rm_md === "RM" ? <span className="font-mono text-[8px] tracking-[0.14em] px-1.5 py-px bg-sky-500/20 text-sky-100 border border-sky-500/40">RM</span> : null}
        {node.dual_role ? <span className="font-mono text-[8px] tracking-[0.14em] px-1.5 py-px bg-purple-500/20 text-purple-100 border border-purple-500/40">DUAL</span> : null}
      </div>
    </button>
  );
}

// Recursive renderer — uses React.createElement to keep babel happy with self-reference.
function renderBranch(node, onClick, filter, depth) {
  const children = node.children || [];
  const card = React.createElement(NodeCard, { node, onClick, filter });
  const vline = React.createElement("div", { className: "w-px h-6 bg-[var(--span-border)]" });
  const childRow = children.length > 0
    ? React.createElement(
        React.Fragment,
        null,
        vline,
        React.createElement(
          "div",
          { className: "flex items-start gap-4" },
          children.map((c) =>
            React.createElement(
              "div",
              { key: c.user_id || c.initials, className: "flex flex-col items-center" },
              renderBranch(c, onClick, filter, depth + 1),
            ),
          ),
        ),
      )
    : null;
  return React.createElement(
    "div",
    { className: "flex flex-col items-center" },
    card,
    childRow,
  );
}

function OrgNodeDrawer(props) {
  const node = props.node;
  const onClose = props.onClose;
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    api.get("/users/" + node.user_id + "/profile").then((r) => setProfile(r.data)).catch(() => setProfile(null));
  }, [node]);
  const p = profile || node;
  const klass = getBand(p.department);
  return (
    <div className="fixed inset-0 z-[150] bg-black/60 backdrop-blur-sm flex items-stretch justify-end" data-testid="org-node-drawer">
      <div className="bg-[var(--span-bg-2)] border-l border-[var(--span-border)] w-full max-w-md overflow-y-auto relative">
        <div className={"p-6 border-b border-[var(--span-border)] " + klass}>
          <div className="flex items-start gap-4">
            <div className="w-14 h-14 flex items-center justify-center font-mono text-[16px] font-bold bg-black/30 border border-current/40 shrink-0">
              {p.initials}
            </div>
            <div className="flex-1">
              <h2 className="font-display text-xl font-bold">{p.name}</h2>
              <div className="text-[12px] opacity-90 mt-0.5">{p.role}</div>
              <div className="text-[11px] opacity-75 font-mono mt-1">{p.email}</div>
              <div className="flex gap-1.5 mt-2">
                {p.rm_md === "MD" ? <span className="font-mono text-[9px] tracking-[0.14em] px-2 py-0.5 bg-amber-500/20 text-amber-100 border border-amber-500/40">MD</span> : null}
                {p.rm_md === "RM" ? <span className="font-mono text-[9px] tracking-[0.14em] px-2 py-0.5 bg-sky-500/20 text-sky-100 border border-sky-500/40">RM</span> : null}
                {p.dual_role ? <span className="font-mono text-[9px] tracking-[0.14em] px-2 py-0.5 bg-purple-500/20 text-purple-100 border border-purple-500/40">DUAL</span> : null}
              </div>
            </div>
          </div>
          <button onClick={onClose} data-testid="org-drawer-close" className="absolute top-4 right-4 text-current/70 hover:text-white">
            <XIcon size={20} />
          </button>
        </div>
        {profile && profile.reporting_line && profile.reporting_line.length > 1 ? (
          <div className="p-6 border-b border-[var(--span-border)]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--span-text-2)] mb-3">Reports to (chain)</div>
            <div className="text-[12px] font-mono flex flex-wrap gap-1.5">
              {profile.reporting_line.slice().reverse().map((u, i, arr) => (
                <span key={u.user_id}>
                  <span className="px-1.5 py-px border border-[var(--span-border)]">{u.initials}</span>
                  {i < arr.length - 1 ? <span className="mx-1 text-[var(--span-text-2)]/50">→</span> : null}
                </span>
              ))}
            </div>
          </div>
        ) : null}
        {profile && profile.direct_reports && profile.direct_reports.length > 0 ? (
          <div className="p-6 border-b border-[var(--span-border)]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--span-text-2)] mb-3">Direct reports ({profile.direct_reports.length})</div>
            <div className="space-y-1.5">
              {profile.direct_reports.map((r) => (
                <div key={r.user_id} className="flex items-center gap-2 text-[12px]">
                  <span className="font-mono text-[10px] px-1.5 py-px border border-[var(--span-border)]">{r.initials}</span>
                  <span>{r.name}</span>
                  <span className="text-[10px] text-[var(--span-text-2)]/70">· {r.role}</span>
                </div>
              ))}
            </div>
          </div>
        ) : null}
        {profile && profile.responsibilities && profile.responsibilities.length > 0 ? (
          <div className="p-6 border-b border-[var(--span-border)]">
            <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--span-text-2)] mb-3">Roles & responsibilities</div>
            <ul className="space-y-2 text-[12px] leading-relaxed">
              {profile.responsibilities.map((r, i) => (
                <li key={i} className="flex gap-2">
                  <span className="text-[var(--span-text-2)]/50 mt-1.5 shrink-0">·</span>
                  <span>{r}</span>
                </li>
              ))}
            </ul>
          </div>
        ) : null}
        <ReassignManagerSection userId={node.user_id} />
      </div>
      <div className="flex-1" onClick={onClose} />
    </div>
  );
}

function ReassignManagerSection({ userId }) {
  const [allUsers, setAllUsers] = useState([]);
  const [managerId, setManagerId] = useState("");
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  useEffect(() => {
    api.get("/org-chart/tree").then(r => {
      const flat = [];
      const walk = (n) => { flat.push(n); (n.children || []).forEach(walk); };
      (r.data.roots || []).forEach(walk);
      setAllUsers(flat);
    }).catch(() => {});
  }, []);
  const save = async () => {
    setMsg(""); setErr("");
    try {
      const validate = await api.post("/org-chart/validate", { user_id: userId, manager_user_id: managerId || null });
      if (!validate.data.ok) { setErr(validate.data.error); return; }
      await api.put(`/org-chart/users/${userId}/manager`, { manager_user_id: managerId || null });
      setMsg("Manager updated. Refresh the org chart to see new lines.");
    } catch (e) { setErr(e?.response?.data?.detail || "Update failed."); }
  };
  return (
    <div className="p-6 border-b border-[var(--span-border)]" data-testid="reassign-manager-section">
      <div className="text-[10px] uppercase tracking-[0.18em] text-[var(--span-text-2)] mb-3">Reassign manager (T4+)</div>
      <select data-testid="reassign-manager-select" value={managerId} onChange={(e) => setManagerId(e.target.value)}
        className="w-full bg-black/30 border border-[var(--span-border)] px-2 py-1.5 text-[12px] font-mono mb-2">
        <option value="">— No manager (top-level) —</option>
        {allUsers.filter(u => u.user_id !== userId).map(u => (
          <option key={u.user_id} value={u.user_id}>{u.name} ({u.initials})</option>
        ))}
      </select>
      <button data-testid="reassign-manager-save" onClick={save}
        className="text-[11px] px-3 py-1 bg-[#F47D20] text-white">Save reporting line</button>
      {msg && <div data-testid="reassign-msg" className="text-[11px] text-emerald-300 mt-2">{msg}</div>}
      {err && <div data-testid="reassign-err" className="text-[11px] text-rose-300 mt-2">{err}</div>}
    </div>
  );
}

export { renderBranch, OrgNodeDrawer };
